import { Flex, Heading, TableRoot, Box, TableBody, TableCell, TableColumnHeader, TableHeader, TableRow, Select, Portal, createListCollection, useBreakpoint } from "@chakra-ui/react";
import { Page } from "../shared/components/layout";
import { FormattedMessage, useIntl } from "react-intl";
import { useContext, useEffect, useState, useMemo } from "react";
import AppContext, { Country, Score } from "../core/app-context";
import { Loading } from "../components/loading";
import { ScoreLine } from "../components/score-line";
import { UseCountries } from "../user/use-countries";
import CountryCombobox from "../components/country-combobox";
import { apiUrl } from "../api/baseUrl";

const HomePage = () => {
  const intl = useIntl();
  const { countries } = UseCountries();
  const { loading, setLoading } = useContext(AppContext);
  const [scores, setScores] = useState<Score[]>([]);
  const [level, setLevel] = useState<string | undefined>("advanced");
  const [length, setLength] = useState<string | undefined>("10");
  const [media, setMedia] = useState<string | undefined>("");
  const [country, setCountry] = useState<Country | undefined>({ code: "", name: "All countries" });
  const [rarity, setRarity] = useState<string>("");
  const countriesList = Array.isArray(countries) ? countries : [];

  const loadScores = async () => {
    setLoading(true)
    const rarityParam = rarity ? `&game__rarity=${rarity}` : ''
    const url = apiUrl(`/api/scores/?game__level=${level}&game__length=${length}&game__media=${media}&game__country=${country?.code}${rarityParam}`)
    const response = await fetch(url, {
      cache: 'no-cache',
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })
    if (response.status === 200) {
      const data = await response.json()
      setScores(data.results)
    } else {
      console.log('Could not load scores.')
    }
    setLoading(false)
  }

  const breakpoint = useBreakpoint({ breakpoints: ['base', 'sm', 'md', 'lg', 'xl', '2xl'] })
  const isMobile = breakpoint === 'base' || breakpoint === 'sm' 

  useEffect(() => {
    loadScores()
  }, [level, length, media, country, rarity]);

  const levels = [
    {value: '', label: 'Any levels'},
    {value: 'beginner', label: 'Beginner'},
    {value: 'advanced', label: 'Advanced'},
    {value: 'expert', label: 'Expert'},
  ];

  const lengths = [
    {value: '', label: 'Any length'},
    {value: '10', label: '10'},
    {value: '20', label: '20'},
    {value: '50', label: '50'},
    {value: '100', label: '100'},
  ];

  const mediums = [
    {value: '', label: 'Any media'},
    {value: 'images', label: 'Images'},
    {value: 'audio', label: 'Sounds'},
    {value: 'video', label: 'Videos'},
  ];

  const rarities = [
    {value: '', label: 'Any rarity'},
    {value: 'familiar', label: 'Familiar'},
    {value: 'regular', label: 'Regular'},
    {value: 'exceptional', label: 'Exceptional'},
  ];

  const mediaCollection = useMemo(() => {
    const items = mediums.map((m, index) => ({
      label: m.label,
      value: m.value,
      original: m,
      index,
    }));
    return createListCollection({ items });
  }, [mediums]);

  const levelCollection = useMemo(() => {
    const items = levels.map((l, index) => ({
      label: l.label,
      value: l.value,
      original: l,
      index,
    }));
    return createListCollection({ items });
  }, [levels]);

  const lengthCollection = useMemo(() => {
    const items = lengths.map((l, index) => ({
      label: l.label,
      value: l.value,
      original: l,
      index,
    }));
    return createListCollection({ items });
  }, [lengths]);

  const rarityCollection = useMemo(() => {
    const items = rarities.map((r, index) => ({
      label: r.label,
      value: r.value,
      original: r,
      index,
    }));
    return createListCollection({ items });
  }, [rarities]);

  const mediaValue = media || '';
  const levelValue = level || '';
  const lengthValue = length || '';
  const rarityValue = rarity || '';

  const handleCountryChange = (c: Country | null) => {
    setCountry(c ?? { code: "", name: "All countries" });
  };

  const handleMediaChange = (details: { value: string[] }) => {
    const selectedValue = details.value[0] || '';
    setMedia(selectedValue);
  };

  const handleLevelChange = (details: { value: string[] }) => {
    const selectedValue = details.value[0] || '';
    setLevel(selectedValue);
  };

  const handleLengthChange = (details: { value: string[] }) => {
    const selectedValue = details.value[0] || '';
    setLength(selectedValue);
  };

  const handleRarityChange = (details: { value: string[] }) => {
    const selectedValue = details.value[0] || '';
    setRarity(selectedValue);
  };

  const renderSelect = (collection: any, value: string | undefined, onValueChange: (details: { value: string[] }) => void, placeholder: string) => (
    <Select.Root
      collection={collection}
      value={value ? [value] : []}
      onValueChange={onValueChange}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText placeholder={placeholder} />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content bg="white" borderRadius="md" borderWidth="2px" borderColor="primary.300" boxShadow="xl" p={1}>
            {collection.items.map((item: any) => (
              <Select.Item key={item.value} item={item}>
                <Select.ItemIndicator />
                <Select.ItemText>{item.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );

  return (
    <Page>
      <Page.Header>
        <Heading color={'gray.800'} size={'lg'} m={0}>
          <FormattedMessage id='hiscores' defaultMessage={'High Scores'}/>
        </Heading>
      </Page.Header>
      <Page.Body>
        <Flex width="full" gap={4} flexDirection={isMobile ? "column" : "row"} flexWrap="wrap">
          <Box minW={isMobile ? "full" : "200px"}>
            <CountryCombobox
              countries={countriesList}
              value={country?.code ? country : null}
              onChange={handleCountryChange}
              allowEmpty
              emptyLabel={intl.formatMessage({ id: "all countries", defaultMessage: "All countries" })}
            />
          </Box>
          {renderSelect(mediaCollection, mediaValue, handleMediaChange, intl.formatMessage({ id: 'select media placeholder', defaultMessage: 'Select media...' }))}
          {renderSelect(levelCollection, levelValue, handleLevelChange, intl.formatMessage({ id: 'select level placeholder', defaultMessage: 'Select level...' }))}
          {renderSelect(lengthCollection, lengthValue, handleLengthChange, intl.formatMessage({ id: 'select length placeholder', defaultMessage: 'Select length...' }))}
          {renderSelect(rarityCollection, rarityValue, handleRarityChange, intl.formatMessage({ id: 'select rarity placeholder', defaultMessage: 'Select rarity...' }))}
        </Flex>
        <>
          {loading ? (
            <Loading/>
          ) : (

            <Box overflowX="auto">
              <TableRoot variant='line' colorPalette='primary' size={['sm', 'md']}>
                <TableHeader>
                  <TableRow bgColor={'primary.200'}>
                  <TableColumnHeader>#</TableColumnHeader>
                  <TableColumnHeader>Player</TableColumnHeader>
                    <TableColumnHeader>
                      flag
                    </TableColumnHeader>
                    <TableColumnHeader>
                      🔭
                    </TableColumnHeader>
                    <TableColumnHeader>Lvl</TableColumnHeader>
                    <TableColumnHeader>n</TableColumnHeader>
                    <TableColumnHeader>Rar</TableColumnHeader>
                    <TableColumnHeader>Score</TableColumnHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scores && scores.map((score, index) => {
                    return <ScoreLine key={index} score={score}/>
                  })}
                </TableBody>
              </TableRoot>
            </Box>
          )}
        </>
      </Page.Body>
    </Page>

  )
};

export default HomePage;